import type { ApsPrefillMappedRow, QuoteTemplateEntry } from '@common/types/quoteTemplates';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { apsPrefillTagsQueryKey, useApsPrefillTags } from '@/queries/templates';
import { applyApsPrefillTags } from '@/services/QuoteTemplatesService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

interface Props {
    template: QuoteTemplateEntry;
    onClose(): void;
}

function mappingKey(templateFormFieldId: string, tag: string): string {
    return `${templateFormFieldId}:${tag}`;
}

function Section({
    title,
    description,
    children,
}: {
    title: string;
    description: string;
    children: ReactNode;
}) {
    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
                {description}
            </Typography>
            {children}
        </Box>
    );
}

function MappingTable({ children, maxHeight = 280 }: { children: ReactNode; maxHeight?: number }) {
    return (
        <TableContainer
            component={Paper}
            sx={{ maxHeight, border: '1px solid', borderColor: 'divider' }}
        >
            <Table stickyHeader size="small">
                {children}
            </Table>
        </TableContainer>
    );
}

function TagCell({ label }: { label: string }) {
    return (
        <TableCell>
            <Chip label={label} size="small" variant="outlined" />
        </TableCell>
    );
}

export default function ConfigureApsTagsDialog({ template, onClose }: Props) {
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useApsPrefillTags(template.quoteTemplateId);
    const [selected, setSelected] = useState<string[]>([]);

    const recommended = data?.recommended ?? [];
    const alreadyMapped = data?.alreadyMapped ?? [];
    const unmappedTags = data?.unmappedTags ?? [];

    useEffect(() => {
        if (!data) return;
        setSelected(data.recommended.map((row) => mappingKey(row.templateFormFieldId, row.tag)));
    }, [data]);

    const applyMutation = useMutation({
        mutationFn: (mappings: Array<{ templateFormFieldId: string; tag: string }>) =>
            applyApsPrefillTags(template.quoteTemplateId, mappings),
        onSuccess: async (response) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['templates'] }),
                queryClient.invalidateQueries({ queryKey: ['quote-template-form-fields'] }),
                queryClient.invalidateQueries({
                    queryKey: apsPrefillTagsQueryKey(template.quoteTemplateId),
                }),
            ]);
            const appliedCount = response.data.appliedCount;
            successSnackbar(
                appliedCount === 1 ? 'Applied 1 APS tag' : `Applied ${appliedCount} APS tags`,
            );
        },
        onError: (error) => {
            console.error(error);
            errorSnackbar('Failed to apply APS tags');
        },
    });

    const allSelected = recommended.length > 0 && selected.length === recommended.length;
    const someSelected = selected.length > 0 && selected.length < recommended.length;

    const handleToggleAll = () => {
        if (allSelected) {
            setSelected([]);
            return;
        }
        setSelected(recommended.map((row) => mappingKey(row.templateFormFieldId, row.tag)));
    };

    const handleToggleRow = (key: string) => {
        setSelected((current) =>
            current.includes(key) ? current.filter((value) => value !== key) : [...current, key],
        );
    };

    const handleApply = () => {
        const selectedMappings = recommended
            .filter((row) => selected.includes(mappingKey(row.templateFormFieldId, row.tag)))
            .map((row) => ({
                templateFormFieldId: row.templateFormFieldId,
                tag: row.tag,
            }));
        if (selectedMappings.length === 0) return;
        applyMutation.mutate(selectedMappings);
    };

    return (
        <Dialog
            open
            onClose={applyMutation.isPending ? undefined : onClose}
            fullWidth
            maxWidth="xl"
        >
            <DialogTitle>Configure Tags</DialogTitle>
            <DialogContent>
                <Stack spacing={3} mt={1}>
                    <Typography variant="body2" color="text.secondary">
                        Intake can prefill a question from the APS when the question{' '}
                        <strong>name</strong> matches the field, or when the question has that
                        prefill <strong>tag</strong>. Recommended adds tags so renamed questions
                        keep working.
                    </Typography>
                    {isLoading && (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    )}
                    {isError && (
                        <Typography color="error">
                            Could not load tag mappings for this template. It may not have an intake
                            form.
                        </Typography>
                    )}
                    {!isLoading && !isError && (
                        <>
                            <Section
                                title={`Already mapped (${alreadyMapped.length})`}
                                description="These questions already receive APS data. Mapped by name uses the canonical question name; mapped by tag uses a configured prefill tag."
                            >
                                {alreadyMapped.length === 0 ? (
                                    <Typography variant="body2">
                                        No questions on this template are mapped yet.
                                    </Typography>
                                ) : (
                                    <MappingTable>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tag</TableCell>
                                                <TableCell>Question</TableCell>
                                                <TableCell>Example APS value</TableCell>
                                                <TableCell>Mapped by</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {alreadyMapped.map((row: ApsPrefillMappedRow) => (
                                                <TableRow
                                                    key={mappingKey(
                                                        row.templateFormFieldId,
                                                        row.tag,
                                                    )}
                                                >
                                                    <TagCell label={row.tagLabel} />
                                                    <TableCell>{row.questionLabel}</TableCell>
                                                    <TableCell>{row.exampleValue}</TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={0.5}>
                                                            {(row.mappedBy === 'name' ||
                                                                row.mappedBy === 'both') && (
                                                                <Chip
                                                                    label="Name"
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {(row.mappedBy === 'tag' ||
                                                                row.mappedBy === 'both') && (
                                                                <Chip
                                                                    label="Tag"
                                                                    size="small"
                                                                    color="success"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </MappingTable>
                                )}
                            </Section>

                            <Section
                                title={`Recommended (${recommended.length})`}
                                description="These questions can be tagged now. Applying a tag moves the row into Already mapped."
                            >
                                {recommended.length === 0 ? (
                                    <Typography variant="body2">
                                        No additional questions can be tagged automatically.
                                    </Typography>
                                ) : (
                                    <MappingTable maxHeight={360}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        indeterminate={someSelected}
                                                        checked={allSelected}
                                                        onChange={handleToggleAll}
                                                        inputProps={{
                                                            'aria-label': 'select all recommended',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>Tag</TableCell>
                                                <TableCell>Question</TableCell>
                                                <TableCell>Example APS value</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recommended.map((row) => {
                                                const key = mappingKey(
                                                    row.templateFormFieldId,
                                                    row.tag,
                                                );
                                                const isSelected = selected.includes(key);
                                                return (
                                                    <TableRow
                                                        key={key}
                                                        hover
                                                        selected={isSelected}
                                                        onClick={() => handleToggleRow(key)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell
                                                            padding="checkbox"
                                                            onClick={(event) =>
                                                                event.stopPropagation()
                                                            }
                                                        >
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() =>
                                                                    handleToggleRow(key)
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TagCell label={row.tagLabel} />
                                                        <TableCell>{row.questionLabel}</TableCell>
                                                        <TableCell>{row.exampleValue}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </MappingTable>
                                )}
                            </Section>

                            <Section
                                title={`Unmapped (${unmappedTags.length})`}
                                description="These APS fields have no matching question on this template. They are candidates to add later, including in bulk."
                            >
                                {unmappedTags.length === 0 ? (
                                    <Typography variant="body2">
                                        Every APS tag is either already mapped or recommended above.
                                    </Typography>
                                ) : (
                                    <MappingTable maxHeight={320}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tag</TableCell>
                                                <TableCell>Recommended question</TableCell>
                                                <TableCell>Example APS value</TableCell>
                                                <TableCell>Type</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {unmappedTags.map((tag) => (
                                                <TableRow key={tag.tag}>
                                                    <TagCell label={tag.tagLabel} />
                                                    <TableCell>{tag.recommendedLabel}</TableCell>
                                                    <TableCell>{tag.exampleValue}</TableCell>
                                                    <TableCell>{tag.questionTypeLabel}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </MappingTable>
                                )}
                            </Section>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={applyMutation.isPending}>
                    Close
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={applyMutation.isPending}
                    disabled={selected.length === 0 || isLoading || isError}
                    onClick={handleApply}
                >
                    Apply Tags
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
